import React from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  Chip,
  List,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  AccessTime as TimeIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import i18n from 'i18n-js';

import PLANS from '../../constants/plans';
import PRICING from '../../constants/pricing';
import { AnalyticsProvider, FetchProvider } from '../../providers';
import { useLoginState } from '../Menu/components/LoginDialog/context';
import {
  Container,
  OfferHeader,
  OfferCard,
  PriceSection,
  OriginalPrice,
  DiscountPrice,
  FeaturesList,
  FeatureItem,
  ActionButton,
  UrgencyBadge,
  TimerContainer,
} from './styles';

export default function DiscountOffer({ open, onClose }) {
  const user = useSelector((state) => state.user);
  const { countryCode, id: userId } = user.data;
  const { openLoginDialog } = useLoginState();

  const prices = PRICING[countryCode] || PRICING['US'];
  const proPlan = PLANS.find(plan => plan.id === 'pro');
  const discountPercent = 30;

  // Calculate prices
  const originalYearlyPrice = parseFloat(prices.pro.yearly.replace(/[^\d.]/g, ''));
  const discountedYearlyPrice = Math.round(originalYearlyPrice * (1 - discountPercent / 100));
  const originalMonthlyPrice = Math.round(originalYearlyPrice / 12);
  const discountedMonthlyPrice = Math.round(discountedYearlyPrice / 12);
  
  const currency = prices.pro.monthly.replace(/[\d.,\s]/g, '');
  const savings = originalYearlyPrice - discountedYearlyPrice;

  const formatPrice = (amount, currency) => {
    if (currency === '€') {
      return `${amount}€`;
    }
    return `${currency}${amount}`;
  };

  const handleSelectPlan = async () => {
    try {
      if (!userId) {
        openLoginDialog();
        return;
      }

      const currencyCode = currency === '€' ? 'eur' : 'usd';
      
      const result = await FetchProvider.fetchPost(
        'stripe/create-checkout-session',
        {
          countryCode,
          plan: 'pro',
          period: 'yearly',
          currency: currencyCode,
          onboarding: true,
          discountPercent: discountPercent,
        }
      );

      let stripe = Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY); // eslint-disable-line
      stripe.redirectToCheckout({ sessionId: result.sessionId });

      AnalyticsProvider.event('ONBOARDING_DISCOUNT_ACCEPTED', { 
        plan: 'pro',
        period: 'yearly',
        discountPercent,
        currency: currencyCode
      });
    } catch (e) {
      console.error('Error creating checkout session:', e);
    }
  };

  const handleClose = () => {
    AnalyticsProvider.event('ONBOARDING_DISCOUNT_DECLINED', { 
      plan: 'pro',
      discountPercent
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ 
        '& .MuiDialog-paper': { 
          position: 'relative',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        } 
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1000,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Container>
          <OfferHeader>
            <UrgencyBadge>
              <TimeIcon fontSize="small" />
              <Typography variant="caption" fontWeight="bold">
                {i18n.t('discountOffer.limitedTime')}
              </Typography>
            </UrgencyBadge>
            
            <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
              {i18n.t('discountOffer.title')}
            </Typography>
            
            <Typography variant="h6" align="center" sx={{ opacity: 0.9 }}>
              {i18n.t('discountOffer.subtitle')}
            </Typography>
          </OfferHeader>

          <OfferCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <OfferIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Chip
                label={`${discountPercent}% OFF`}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 1,
                }}
              />
            </Box>

            <Typography variant="h5" fontWeight="bold" align="center" gutterBottom>
              {i18n.t('pricing.plans.pro.name')}
            </Typography>

            <PriceSection>
              <OriginalPrice>
                <Typography variant="h6" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                  {formatPrice(originalMonthlyPrice, currency)}/{i18n.t('discountOffer.month')}
                </Typography>
                <Typography variant="body2" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                  ({formatPrice(originalYearlyPrice, currency)} {i18n.t('discountOffer.per')} {i18n.t('discountOffer.year')})
                </Typography>
              </OriginalPrice>
              
              <DiscountPrice>
                <Typography variant="h3" fontWeight="bold">
                  {formatPrice(discountedMonthlyPrice, currency)}
                </Typography>
                <Typography variant="body1">
                  /{i18n.t('discountOffer.month')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  ({formatPrice(discountedYearlyPrice, currency)} {i18n.t('discountOffer.per')} {i18n.t('discountOffer.year')})
                </Typography>
              </DiscountPrice>

              <Typography variant="body1" align="center" sx={{ color: '#4caf50', fontWeight: 'bold', mt: 1 }}>
                {i18n.t('discountOffer.savings', { amount: formatPrice(savings, currency) })}
              </Typography>
            </PriceSection>

            <FeaturesList>
              <Typography variant="h6" gutterBottom align="center">
                {i18n.t('discountOffer.includes')}
              </Typography>
              <List disablePadding>
                {proPlan.features.included.map((feature) => (
                  <FeatureItem key={feature}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: '#4caf50' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={i18n.t(`pricing.features.${feature}`)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.primary',
                      }}
                    />
                  </FeatureItem>
                ))}
              </List>
            </FeaturesList>

            <ActionButton>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSelectPlan}
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #45a049 30%, #5da85d 90%)',
                  },
                }}
              >
                {i18n.t('discountOffer.claimOffer')}
              </Button>
              
              <Typography variant="caption" align="center" sx={{ mt: 1, opacity: 0.8 }}>
                {i18n.t('discountOffer.oneTimeOnly')}
              </Typography>
            </ActionButton>
          </OfferCard>
        </Container>
      </DialogContent>
    </Dialog>
  );
}